import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import type { QuickQuoteCreate, QuoteType } from '@techgitter/contracts';
import { createQuickQuote } from '../../services/quotesApi';

// Walk-in quote types — same options the QuotesPage list offers.
// `replacement` is retained server-side for back-compat but isn't a
// new-quote option in the UI.
const QUOTE_TYPE_LABEL: Record<QuoteType, string> = {
  new_laptop: 'New Laptop',
  new_desktop: 'New Desktop',
  new_desktop_custom: 'New Desktop (Custom)',
  new_all_in_one: 'New All-in-One',
  repair: 'Repair Service',
  network_install: 'Network Installation',
  m365: 'M365 Onboarding',
  replacement: 'New Computer',
};
const QUOTE_TYPE_OPTIONS: QuoteType[] = [
  'new_laptop',
  'new_desktop',
  'new_desktop_custom',
  'new_all_in_one',
  'repair',
  'network_install',
  'm365',
];

interface QuickQuoteDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called with the new quote's number. Caller typically navigates to it. */
  onCreated: (quoteNumber: string) => void;
}

interface FormState {
  type: QuoteType;
  firstName: string;
  lastName: string;
  businessName: string;
  email: string;
  phone: string;
  mobile: string;
}

const EMPTY: FormState = {
  type: 'repair',
  firstName: '',
  lastName: '',
  businessName: '',
  email: '',
  phone: '',
  mobile: '',
};

export const QuickQuoteDialog: React.FC<QuickQuoteDialogProps> = ({
  open,
  onClose,
  onCreated,
}) => {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setField = useCallback(
    <K extends keyof FormState>(key: K, value: FormState[K]) => {
      setForm((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  // Mirrors the backend's CustomerService.create_customer validation so
  // the user sees the gate before the round-trip. A name is one of
  // firstName / businessName; contact is one of email / phone / mobile.
  const hasName =
    form.firstName.trim().length > 0 || form.businessName.trim().length > 0;
  const hasContact =
    form.email.trim().length > 0 ||
    form.phone.trim().length > 0 ||
    form.mobile.trim().length > 0;
  const canSubmit = hasName && hasContact && !submitting;

  const helperContact = useMemo(
    () => (hasContact ? ' ' : 'Email, phone, or mobile required'),
    [hasContact],
  );

  const handleClose = useCallback(() => {
    if (submitting) return;
    setForm(EMPTY);
    setError(null);
    onClose();
  }, [onClose, submitting]);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const trimmed = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        businessName: form.businessName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        mobile: form.mobile.trim(),
      };
      const payload: QuickQuoteCreate = {
        type: form.type,
        // GST display defaults to inc-GST server-side; tech can flip
        // per-quote on the detail page when the customer prefers ex-GST.
        ...(trimmed.firstName ? { firstName: trimmed.firstName } : {}),
        ...(trimmed.lastName ? { lastName: trimmed.lastName } : {}),
        ...(trimmed.businessName ? { businessName: trimmed.businessName } : {}),
        ...(trimmed.email ? { email: trimmed.email } : {}),
        ...(trimmed.phone ? { phone: trimmed.phone } : {}),
        ...(trimmed.mobile ? { mobile: trimmed.mobile } : {}),
      };
      const result = await createQuickQuote(payload);
      setForm(EMPTY);
      onCreated(result.quote.number);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Quick Quote</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
          Create a draft quote and customer in one step. Use this for walk-ins,
          phone calls, or website queries when the customer isn't in the system
          yet — you can fill in the rest of their profile later.
        </Typography>

        <TextField
          select
          label="Quote type"
          value={form.type}
          onChange={(e) => setField('type', e.target.value as QuoteType)}
          fullWidth
          size="small"
          disabled={submitting}
          sx={{ mb: 2 }}
        >
          {QUOTE_TYPE_OPTIONS.map((t) => (
            <MenuItem key={t} value={t}>
              {QUOTE_TYPE_LABEL[t]}
            </MenuItem>
          ))}
        </TextField>

        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField
            label="First name"
            value={form.firstName}
            onChange={(e) => setField('firstName', e.target.value)}
            required={!form.businessName.trim()}
            error={!hasName}
            helperText={!hasName ? 'First name or business name required' : ' '}
            size="small"
            disabled={submitting}
            fullWidth
            autoFocus
          />
          <TextField
            label="Last name"
            value={form.lastName}
            onChange={(e) => setField('lastName', e.target.value)}
            size="small"
            disabled={submitting}
            fullWidth
          />
        </Box>

        <TextField
          label="Business name"
          value={form.businessName}
          onChange={(e) => setField('businessName', e.target.value)}
          size="small"
          disabled={submitting}
          fullWidth
          helperText="Optional — fill in for business customers"
          sx={{ mb: 2 }}
        />

        <TextField
          label="Email"
          type="email"
          value={form.email}
          onChange={(e) => setField('email', e.target.value)}
          size="small"
          disabled={submitting}
          fullWidth
          error={!hasContact}
          helperText={helperContact}
          sx={{ mb: 2 }}
        />

        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField
            label="Phone"
            value={form.phone}
            onChange={(e) => setField('phone', e.target.value)}
            size="small"
            disabled={submitting}
            fullWidth
            error={!hasContact}
            helperText={helperContact}
          />
          <TextField
            label="Mobile"
            value={form.mobile}
            onChange={(e) => setField('mobile', e.target.value)}
            size="small"
            disabled={submitting}
            fullWidth
            error={!hasContact}
            helperText={helperContact}
          />
        </Box>

        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {submitting ? <CircularProgress size={16} /> : 'Create Quote'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuickQuoteDialog;
